import { DestroyRef, inject, Injectable, Injector, signal } from '@angular/core';
import type { OverlayRef } from '@angular/cdk/overlay';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { timer } from 'rxjs';
export interface ToastItem {
  id: number;
  message: string;
  alertClass: string;
}

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private injector = inject(Injector);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);
  toasts = signal<ToastItem[]>([]);
  private overlayRef: OverlayRef | null = null;
  private overlayLoading: Promise<void> | null = null;
  private toastId = 0;

  constructor() {
    this.destroyRef.onDestroy(() => this.overlayRef?.dispose());
  }

  success(message: string, duration = 3000) {
    this.showToast(message, 'alert-success', duration);
  }
  error(message?: string, duration = 3000) {
    const msg =
      message || this.translate.instant('common.errors.default_error_message');
    this.showToast(msg, 'alert-error', duration);
  }

  warning(message: string, duration = 3000) {
    this.showToast(message, 'alert-warning', duration);
  }
  private showToast(message: string, alertClass: string, duration: number) {
    const id = this.toastId++;
    this.toasts.update((list) => [...list, { id, message, alertClass }]);
    // the auto-close timer starts after the toast appears, not while its chunk is loading
    this.attachOverlay().then(() =>
      timer(duration)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.remove(id)),
    );
  }
  remove(id: number) {
    this.toasts.update((list) => list.filter((t) => t.id !== id));

    if (this.toasts().length === 0 && this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }
  // CDK Overlay + the container are a lazy chunk: they load on the first toast, not at startup.
  // Resolves when the toasts are on screen (or the chunk failed — then they just expire).
  private attachOverlay(): Promise<void> {
    if (this.overlayRef) return Promise.resolve();
    this.overlayLoading ??= import('./toast-container')
      .then(({ attachToastContainer }) => {
        // the toasts may have been closed while the chunk was loading
        if (this.toasts().length && !this.overlayRef) {
          this.overlayRef = attachToastContainer(this.injector);
        }
      })
      .catch((e) => console.error('Toast container failed to load', e))
      .finally(() => (this.overlayLoading = null));
    return this.overlayLoading;
  }
}
